/**
 * The shared-funder map as positions: three columns, no physics.
 *
 * A map item holds an anchor, the funders that reached it, the intermediaries
 * set aside, and the members that share those funders, each member naming
 * which. That is a bipartite graph between funders and members with the
 * anchor beside it, and a bipartite graph reads best as two columns with the
 * edges between them: labels never collide, a column sorts by how connected
 * each node is, and a reader traces one node's edges by lighting them. A
 * force layout would put the same nodes in a ball whose positions mean
 * nothing, so it is not used. This module is pure: the same item and options
 * give the same picture.
 */
import { CO_FUNDING_DEFAULTS } from '../../../src/recipes/defaults.js';
import type { CoalitionMapItem } from '../../../src/serve/items.js';
import { formatEin } from '../format.js';
import { displayName } from '../names.js';

export type NodeKind = 'anchor' | 'funder' | 'member' | 'excluded';

export interface MapNode {
  readonly key: string;
  readonly kind: NodeKind;
  readonly ein: string | null;
  readonly name: string | null;
  readonly label: string;
  readonly listed: boolean;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  /** For a funder, the shown members that share it; for a member, its shown shared funders. */
  readonly degree: number;
}

export interface MapEdge {
  readonly funder: string;
  readonly member: string;
}

export interface MapLayout {
  readonly width: number;
  readonly height: number;
  readonly anchor: MapNode;
  readonly funders: readonly MapNode[];
  readonly members: readonly MapNode[];
  readonly excluded: readonly MapNode[];
  readonly edges: readonly MapEdge[];
  /** The most funders any member of the item shares, for the control's range. */
  readonly maximumShared: number;
  /** Members of the item below the minimum, so the page can say how many it is not showing. */
  readonly hiddenMembers: number;
}

export interface LayoutOptions {
  readonly width?: number;
  readonly minimumShared?: number;
  readonly showExcluded?: boolean;
  readonly query?: string;
  readonly limit?: number;
}

export const ROW_HEIGHT = 22;
export const PADDING = 48;
export const DEFAULT_WIDTH = 800;
// The columns sit where the labels fit: a label truncated to 34 characters at 12 units of font measures
// about 214 units on the national data, and the specification's 22% left 164 and clipped the longest
// names at the edge of the drawing. 30% leaves 228 beside each column and still a clear middle.
const FUNDER_COLUMN = 0.3;
const MEMBER_COLUMN = 0.7;
const MIN_RADIUS = 3;
const MAX_RADIUS = 8;

/**
 * The recipe's own minimum and ceiling, read from the parameters it recorded on
 * the map item; the fallbacks are the co-funding recipe's own defaults.
 */
export const recipeParameters = (parameters: string): { minimumShared: number; ceiling: number } => {
  try {
    const parsed = JSON.parse(parameters) as { minimumSharedFunders?: number; maximumFanOut?: number };
    return { minimumShared: parsed.minimumSharedFunders ?? CO_FUNDING_DEFAULTS.minimumSharedFunders, ceiling: parsed.maximumFanOut ?? CO_FUNDING_DEFAULTS.maximumFanOut };
  } catch {
    return { minimumShared: CO_FUNDING_DEFAULTS.minimumSharedFunders, ceiling: CO_FUNDING_DEFAULTS.maximumFanOut };
  }
};

export const ANCHOR_KEY = 'anchor';
export const funderKey = (ein: string): string => `f:${ein}`;
export const memberKey = (ein: string): string => `m:${ein}`;
const excludedKey = (ein: string): string => `x:${ein}`;

const labelOf = (name: string | null, ein: string | null): string => displayName(name) ?? (ein === null ? 'Unnamed' : formatEin(ein));

const compare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

const radius = (degree: number, maximum: number): number => MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * (maximum <= 0 ? 0 : degree / maximum);

interface Candidate {
  readonly ein: string;
  readonly name: string | null;
  readonly listed: boolean;
}

export const layoutSharedFunders = (map: CoalitionMapItem, options: LayoutOptions = {}): MapLayout => {
  const width = options.width ?? DEFAULT_WIDTH;
  const minimumShared = Math.max(1, options.minimumShared ?? 2);
  const query = (options.query ?? '').trim().toLowerCase();
  // An EIN is printed with a hyphen and stored without one; the digits alone match either way of typing it.
  const digits = query.replace(/\D/g, '');
  const limit = options.limit ?? Number.POSITIVE_INFINITY;

  const funderByEin = new Map<string, Candidate>(map.funders.filter((funder) => funder.ein !== null).map((funder) => [funder.ein ?? '', { ein: funder.ein ?? '', name: funder.name, listed: funder.listed }]));
  const matches = (name: string | null, ein: string): boolean =>
    query.length === 0 || labelOf(name, ein).toLowerCase().includes(query) || (name ?? '').toLowerCase().includes(query) || ein.includes(query) || (digits.length > 0 && ein.includes(digits));

  // Members at or above the minimum; then, with a query, those that match or share a matching funder.
  const eligible = map.members.filter((member) => member.organization.ein !== null && member.sharedFunders.length >= minimumShared);
  const matchingFunders = new Set([...funderByEin.values()].filter((funder) => matches(funder.name, funder.ein)).map((funder) => funder.ein));
  const matchingMembers = new Set(eligible.filter((member) => matches(member.organization.name, member.organization.ein ?? '')).map((member) => member.organization.ein ?? ''));
  const keptMembers =
    query.length === 0
      ? eligible
      : eligible.filter((member) => matchingMembers.has(member.organization.ein ?? '') || member.sharedFunders.some((ein) => matchingFunders.has(ein)));
  // Funders a shown member shares; with a query, those that match or are shared by a matching member.
  const sharedByKept = new Map<string, number>();
  for (const member of keptMembers) {
    const memberMatches = query.length === 0 || matchingMembers.has(member.organization.ein ?? '');
    for (const ein of member.sharedFunders) {
      if (!funderByEin.has(ein)) {
        continue;
      }
      if (query.length === 0 || matchingFunders.has(ein) || memberMatches) {
        sharedByKept.set(ein, (sharedByKept.get(ein) ?? 0) + 1);
      }
    }
  }

  const funders = [...funderByEin.values()]
    .filter((candidate) => sharedByKept.has(candidate.ein))
    .map((candidate) => ({ candidate, degree: sharedByKept.get(candidate.ein) ?? 0 }))
    .sort((a, b) => b.degree - a.degree || compare(labelOf(a.candidate.name, a.candidate.ein), labelOf(b.candidate.name, b.candidate.ein)))
    .slice(0, limit);
  const funderSet = new Set(funders.map((entry) => entry.candidate.ein));
  const members = keptMembers
    .map((member) => ({ member, degree: member.sharedFunders.filter((ein) => funderSet.has(ein)).length }))
    .filter((entry) => entry.degree > 0)
    .sort((a, b) => b.degree - a.degree || compare(labelOf(a.member.organization.name, a.member.organization.ein ?? ''), labelOf(b.member.organization.name, b.member.organization.ein ?? '')))
    .slice(0, limit);
  const memberSet = new Set(members.map((entry) => entry.member.organization.ein ?? ''));
  // Degrees are counted once more over the final columns, so a limit never leaves a funder claiming members that are not drawn.
  const funderDegree = new Map<string, number>();
  const edges: MapEdge[] = [];
  for (const entry of members) {
    for (const ein of entry.member.sharedFunders) {
      if (funderSet.has(ein) && memberSet.has(entry.member.organization.ein ?? '')) {
        edges.push({ funder: funderKey(ein), member: memberKey(entry.member.organization.ein ?? '') });
        funderDegree.set(ein, (funderDegree.get(ein) ?? 0) + 1);
      }
    }
  }

  const funderX = Math.round(width * FUNDER_COLUMN);
  const memberX = Math.round(width * MEMBER_COLUMN);
  const maxFunderDegree = Math.max(0, ...funders.map((entry) => funderDegree.get(entry.candidate.ein) ?? 0));
  const maxMemberDegree = Math.max(0, ...members.map((entry) => entry.degree));
  const funderNodes: MapNode[] = funders.map((entry, index) => {
    const degree = funderDegree.get(entry.candidate.ein) ?? 0;
    return {
      key: funderKey(entry.candidate.ein),
      kind: 'funder',
      ein: entry.candidate.ein,
      name: entry.candidate.name,
      label: labelOf(entry.candidate.name, entry.candidate.ein),
      listed: entry.candidate.listed,
      x: funderX,
      y: PADDING + index * ROW_HEIGHT,
      r: radius(degree, maxFunderDegree),
      degree,
    };
  });
  const memberNodes: MapNode[] = members.map((entry, index) => ({
    key: memberKey(entry.member.organization.ein ?? ''),
    kind: 'member',
    ein: entry.member.organization.ein,
    name: entry.member.organization.name,
    label: labelOf(entry.member.organization.name, entry.member.organization.ein),
    listed: entry.member.organization.listed,
    x: memberX,
    y: PADDING + index * ROW_HEIGHT,
    r: radius(entry.degree, maxMemberDegree),
    degree: entry.degree,
  }));
  const excludedNodes: MapNode[] = options.showExcluded
    ? map.excluded
        .filter((funder) => funder.ein !== null)
        .map((funder, index) => ({
          key: excludedKey(funder.ein ?? ''),
          kind: 'excluded' as const,
          ein: funder.ein,
          name: funder.name,
          label: labelOf(funder.name, funder.ein),
          listed: funder.listed,
          x: funderX,
          y: PADDING + (funderNodes.length + index) * ROW_HEIGHT,
          r: MIN_RADIUS,
          degree: 0,
        }))
    : [];

  const rows = Math.max(1, funderNodes.length + excludedNodes.length, memberNodes.length);
  const height = PADDING * 2 + rows * ROW_HEIGHT;
  const anchor: MapNode = {
    key: ANCHOR_KEY,
    kind: 'anchor',
    ein: map.anchor.ein,
    name: map.anchor.name,
    label: labelOf(map.anchor.name, map.anchor.ein),
    listed: map.anchor.listed,
    x: Math.round(width / 2),
    y: Math.round(height / 2),
    r: MAX_RADIUS + 2,
    degree: funderNodes.length,
  };
  return {
    width,
    height,
    anchor,
    funders: funderNodes,
    members: memberNodes,
    excluded: excludedNodes,
    edges,
    maximumShared: Math.max(0, ...map.members.map((member) => member.sharedFunders.length)),
    hiddenMembers: map.members.length - eligible.length,
  };
};

export type Direction = 'up' | 'down' | 'left' | 'right';

/**
 * The node the arrow keys reach from a node: up and down walk a column, with
 * the ghosts continuing the funder column; left and right cross to the same
 * row of the other column; the anchor steps to the top of either column; an
 * end stays put. Null for a key the layout does not hold.
 */
export const nextNodeKey = (layout: MapLayout, key: string, direction: Direction): string | null => {
  const left = [...layout.funders, ...layout.excluded];
  const right = layout.members;
  if (key === ANCHOR_KEY) {
    return direction === 'left' ? (left[0]?.key ?? null) : direction === 'right' ? (right[0]?.key ?? null) : ANCHOR_KEY;
  }
  const inLeft = left.findIndex((node) => node.key === key);
  const inRight = right.findIndex((node) => node.key === key);
  if (inLeft < 0 && inRight < 0) {
    return null;
  }
  const column = inLeft >= 0 ? left : right;
  const index = inLeft >= 0 ? inLeft : inRight;
  switch (direction) {
    case 'up':
      return column[Math.max(0, index - 1)]?.key ?? key;
    case 'down':
      return column[Math.min(column.length - 1, index + 1)]?.key ?? key;
    case 'left':
      return inLeft >= 0 ? key : (left[Math.min(index, left.length - 1)]?.key ?? key);
    case 'right':
      return inRight >= 0 ? key : (right[Math.min(index, right.length - 1)]?.key ?? key);
  }
};
